/* eslint-disable no-unused-vars */
import { useEffect, useRef } from 'react';
import camelCase from 'camelcase-keys';
import { useDispatch, useSelector } from 'react-redux';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { AIVOICE_NOTIFICATION_URL } from '@src/configs';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';

const useNotificationSSE = () => {
  const dispatch = useDispatch();
  const { accessToken, user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const isNotificationEnabled = getFeatureValue(FEATURE_KEYS.NOTIFICATION, {
    userId: user?.id,
    email: user?.email,
  });
  const eventSourceRef = useRef(null);
  const accessTokenRef = useRef(accessToken);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    if (!isNotificationEnabled) return;

    const url = `${AIVOICE_NOTIFICATION_URL}/api/v1/notifications/subscribe`;

    const createConnection = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const eventSource = new EventSourcePolyfill(url, {
        headers: {
          Authorization: `Bearer ${accessTokenRef.current}`,
        },
        heartbeatTimeout: 60000,
      });

      eventSource.addEventListener('notification', (event) => {
        try {
          const data = camelCase(JSON.parse(event.data));
          const notification = {
            id: data.notificationId,
            rawContent: data.content,
            sentAt: data.sendTime,
            isPinned: false,
            readAt: null,
          };
          dispatch(
            actions.notification.receiveNotification({
              groupCode: data.groupCode,
              notification,
            }),
          );
        } catch (error) {
          // Ignore malformed events
        }
      });

      eventSourceRef.current = eventSource;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const isConnectionDead =
        !eventSourceRef.current ||
        eventSourceRef.current.readyState === EventSourcePolyfill.CLOSED;

      dispatch(actions.notification.fetchGroupsUnreadStatus());
      if (isConnectionDead) {
        createConnection();
      }
    };

    createConnection();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [accessToken, isNotificationEnabled]);
};

export default useNotificationSSE;
